import React, { useState, useEffect, useRef } from 'react';
import { getData, postData, putData, deleteData, API_BASE_URL } from '../helpers/ApiSettings';
import { Editor } from '@tinymce/tinymce-react';

const Pages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPage, setEditingPage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    isPublished: true,
    featured_image: null,
    existing_image_id: null 
  });
  const [imagePreview, setImagePreview] = useState('');
  const editorRef = useRef(null);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await getData('/pages?sort[0]=title:asc&populate=featured_image');
      setPages(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des pages');
      console.error('Error fetching pages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        setFormData(prev => ({
          ...prev,
          featured_image: file,
          existing_image_id: null
        }));
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
      } else {
        setFormData(prev => ({
          ...prev,
          featured_image: null
        }));
        if (editingPage?.featured_image?.url) {
          setImagePreview(`${API_BASE_URL}${editingPage.featured_image.url}`);
        } else {
          setImagePreview('');
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('files', file);
    
    try {
      const response = await postData('/upload', formData);
      return response.data[0].id;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let imageId = formData.existing_image_id;
      
      if (formData.featured_image instanceof File) {
        imageId = await uploadImage(formData.featured_image);
      }
      
      const pageData = {
        title: formData.title,
        slug: formData.slug,
        content: editorRef.current.getContent(),
        isPublished: formData.isPublished,
        featured_image: imageId
      };

      if (editingPage) {
        await putData(`/pages/${editingPage.documentId}`, { data: pageData });
        alert('Page modifiée avec succès');
      } else {
        await postData('/pages', { data: pageData });
        alert('Page créée avec succès');
      }
      
      handleCloseModal();
      await fetchPages();
    } catch (err) {
      alert('Erreur lors de l\'enregistrement de la page');
      console.error('Error saving page:', err);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setFormData({ 
      title: '', 
      slug: '', 
      content: '', 
      isPublished: true, 
      featured_image: null,
      existing_image_id: null 
    });
    setImagePreview('');
    setEditingPage(null);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content,
      isPublished: page.isPublished,
      featured_image: null,
      existing_image_id: page.featured_image?.id || null
    });
    
    if (page.featured_image?.url) {
      setImagePreview(`${API_BASE_URL}${page.featured_image.url}`);
    } else {
      setImagePreview('');
    }
    
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette page ?')) {
      try {
        await deleteData(`/pages/${id}`);
        await fetchPages();
        alert('Page supprimée avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression de la page');
        console.error('Error deleting page:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingPage(null);
    setFormData({ 
      title: '', 
      slug: '', 
      content: '', 
      isPublished: true,
      featured_image: null,
      existing_image_id: null
    });
    setImagePreview('');
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Pages</h2>
        <div>
          <button 
            onClick={fetchPages} 
            className="btn btn-outline-primary me-2"
          >
            Actualiser
          </button>
          <button 
            onClick={handleAddNew}
            className="btn btn-primary"
          >
            Nouvelle Page
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {pages.length === 0 ? (
            <p className="text-center text-muted my-5">Aucune page pour le moment</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Titre</th>
                    <th>URL</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((page) => (
                    <tr key={page.id}>
                      <td>
                        {page.featured_image?.url ? (
                          <img 
                            src={API_BASE_URL + page.featured_image.url} 
                            alt={page.title}
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          />
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </td>
                      <td>{page.title}</td>
                      <td><code>{page.slug}</code></td>
                      <td>
                        <span className={`badge ${page.isPublished ? 'bg-success' : 'bg-warning'}`}>
                          {page.isPublished ? 'Publié' : 'Brouillon'}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(page)}
                          >
                            Modifier
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(page.documentId)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className={`modal ${modalOpen ? 'show' : ''}`}
         style={{
           display: modalOpen ? 'block' : 'none',
           backgroundColor: 'rgba(0,0,0,0.5)',
           position: 'fixed',
           top: 0,
           left: 0,
           width: '100%',
           height: '100%',
           zIndex: 1050,
           overflow: 'auto'
         }}
         id="pageModal" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingPage ? 'Modifier la Page' : 'Nouvelle Page'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="featured_image" className="form-label">Image</label>
                  <input
                    type="file"
                    className="form-control"
                    id="featured_image"
                    name="featured_image"
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                  {imagePreview && (
                    <div className="mt-2">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                        className="img-thumbnail"
                      />
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label htmlFor="title" className="form-label">Titre</label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="slug" className="form-label">URL (slug)</label>
                  <input
                    type="text"
                    className="form-control"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                  />
                  <small className="text-muted">Ex: about-us, contact, services</small>
                </div>
                <div className="mb-3">
                  <label htmlFor="content" className="form-label">Contenu</label>
                  <Editor
                    onInit={(evt, editor) => editorRef.current = editor}
                    initialValue={editingPage?.content || ''}
                    init={{
                      height: 400,
                      menubar: true,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | table | help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                  />
                </div>
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isPublished"
                    name="isPublished"
                    checked={formData.isPublished}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="isPublished">
                    Publier la page
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPage ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pages;